import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign, Calendar, AlertTriangle, Building2, Users, FileText,
  TrendingUp, TrendingDown, Clock, CheckCircle, XCircle, ArrowRight,
  MapPin, Phone, Mail, Home, Store, Warehouse, Briefcase
} from 'lucide-react';

const Overview = () => {
  // Mock data - Saudi Arabia focused
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
  
  const stats = {
    totalProperties: 12,
    occupiedProperties: 9,
    vacantProperties: 2,
    maintenanceProperties: 1,
    totalTenants: 9,
    totalRentOut: 186000,
    totalRentIn: 45000,
    netRevenue: 141000,
    rentsDueThisMonth: 186000,
    rentsPaid: 141000,
    rentsPending: 45000,
    rentsOverdue: 0,
    contractsExpiring30Days: 2,
    contractsExpiring90Days: 5,
    activeIssues: 6,
    resolvedIssuesThisMonth: 12,
  };

  // Quick Stats matching Financial/Assets pattern
  const quickStats = [
    {
      label: 'Net Monthly Revenue',
      value: `SAR ${stats.netRevenue.toLocaleString()}`,
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: 'Rents Due This Month',
      value: `SAR ${stats.rentsDueThisMonth.toLocaleString()}`,
      change: `${((stats.rentsPending / stats.rentsDueThisMonth) * 100).toFixed(0)}% pending`,
      trend: stats.rentsPending > 0 ? 'down' : 'up',
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'Contracts Expiring (30 days)',
      value: stats.contractsExpiring30Days.toString(),
      change: `${stats.contractsExpiring90Days} in 90 days`,
      trend: stats.contractsExpiring30Days > 0 ? 'warning' : 'up',
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      label: 'Active Issues',
      value: stats.activeIssues.toString(),
      change: `${stats.resolvedIssuesThisMonth} resolved`,
      trend: stats.activeIssues > 5 ? 'warning' : 'up',
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
  ];

  // Rents due this month
  const rentsDueThisMonth = [
    {
      id: 'RENT-001',
      propertyName: 'Riyadh Main Store',
      tenantName: 'ABC Retail Company',
      amount: 15000,
      dueDate: '2024-02-15',
      daysUntilDue: 2,
      status: 'pending',
      propertyType: 'store',
      city: 'Riyadh'
    },
    {
      id: 'RENT-002',
      propertyName: 'Jeddah Warehouse Complex',
      tenantName: 'XYZ Logistics',
      amount: 25000,
      dueDate: '2024-02-20',
      daysUntilDue: 7,
      status: 'pending',
      propertyType: 'warehouse',
      city: 'Jeddah'
    },
    {
      id: 'RENT-003',
      propertyName: 'Dammam Office Building',
      tenantName: 'Tech Solutions Inc.',
      amount: 18000,
      dueDate: '2024-02-10',
      daysUntilDue: -3,
      status: 'overdue',
      propertyType: 'building',
      city: 'Dammam'
    },
    {
      id: 'RENT-004',
      propertyName: 'Khobar Storage Facility',
      tenantName: 'Storage Co.',
      amount: 20000,
      dueDate: '2024-02-15',
      daysUntilDue: 2,
      status: 'pending',
      propertyType: 'warehouse',
      city: 'Khobar'
    },
  ];

  // Rents we pay
  const rentsWePay = [
    {
      id: 'PAY-001',
      propertyName: 'Corporate Office - Olaya',
      landlordName: 'Al-Rashid Properties',
      amount: 25000,
      dueDate: '2024-02-05',
      daysUntilDue: -8,
      status: 'overdue',
      propertyType: 'office',
      city: 'Riyadh',
      contractEnd: '2025-12-31'
    },
    {
      id: 'PAY-002',
      propertyName: 'Warehouse - Dammam Industrial',
      landlordName: 'Industrial Real Estate Co.',
      amount: 20000,
      dueDate: '2024-02-12',
      daysUntilDue: -1,
      status: 'overdue',
      propertyType: 'warehouse',
      city: 'Dammam',
      contractEnd: '2026-06-30'
    },
  ];

  // Expiring contracts
  const expiringContracts = [
    {
      id: 'CNT-001',
      propertyName: 'Riyadh Retail Space',
      tenantName: 'Fashion Store Co.',
      contractEnd: '2024-03-15',
      daysUntilExpiry: 30,
      monthlyRent: 12000,
      status: 'expiring-soon',
      type: 'renting-out'
    },
    {
      id: 'CNT-002',
      propertyName: 'Jeddah Warehouse Complex',
      tenantName: 'XYZ Logistics',
      contractEnd: '2024-03-20',
      daysUntilExpiry: 35,
      monthlyRent: 25000,
      status: 'expiring-soon',
      type: 'renting-out'
    },
  ];

  // Recent activity
  const recentActivity = [
    { id: 1, type: 'payment', message: 'Payment received from ABC Retail Company - SAR 15,000', time: '2 hours ago', icon: CheckCircle, color: 'green' },
    { id: 2, type: 'issue', message: 'New maintenance request: AC Not Working - Dammam Office', time: '5 hours ago', icon: AlertTriangle, color: 'orange' },
    { id: 3, type: 'contract', message: 'Contract signed with New Tenant Co. - Riyadh Store', time: '1 day ago', icon: FileText, color: 'blue' },
    { id: 4, type: 'payment', message: 'Payment sent to Al-Rashid Properties - SAR 25,000', time: '2 days ago', icon: DollarSign, color: 'purple' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-blue-100 text-blue-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'expiring-soon': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'store': return Store;
      case 'warehouse': return Warehouse;
      case 'building': return Building2;
      case 'office': return Briefcase;
      default: return Home;
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Stats - Matching Financial/Assets pattern */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <div className="flex items-center gap-2 mt-2">
                  {stat.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  ) : stat.trend === 'down' ? (
                    <TrendingDown className="w-4 h-4 text-red-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                  )}
                  <span className={`text-sm font-medium ${
                    stat.trend === 'up' ? 'text-green-600' : 
                    stat.trend === 'down' ? 'text-red-600' : 
                    'text-orange-600'
                  }`}>
                    {stat.change}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Renting Out - Properties We Own */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Renting Out</h2>
              <p className="text-sm text-gray-600 mt-1">Properties we own and rent to tenants</p>
            </div>
            <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              {stats.occupiedProperties} Occupied
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Total Monthly Rent Collected</span>
                <span className="text-lg font-bold text-green-600">SAR {stats.totalRentOut.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Paid: SAR {stats.rentsPaid.toLocaleString()}</span>
                <span className="text-red-600">Pending: SAR {stats.rentsPending.toLocaleString()}</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Rents Due This Month</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {rentsDueThisMonth.map((rent) => {
                  const TypeIcon = getTypeIcon(rent.propertyType);
                  return (
                    <motion.div
                      key={rent.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      whileHover={{ scale: 1.01 }}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="p-2 rounded-lg bg-blue-100">
                            <TypeIcon className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{rent.propertyName}</p>
                            <p className="text-xs text-gray-600">{rent.tenantName}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">{rent.city}</span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">Due: {rent.dueDate}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">SAR {rent.amount.toLocaleString()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(rent.status)}`}>
                            {rent.status === 'overdue' ? `${Math.abs(rent.daysUntilDue)} days overdue` :
                             rent.daysUntilDue === 0 ? 'Due today' :
                             `${rent.daysUntilDue} days`}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Renting - Properties We Rent */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Renting</h2>
              <p className="text-sm text-gray-600 mt-1">Properties we rent from landlords</p>
            </div>
            <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
              {rentsWePay.length} Active
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Total Monthly Rent Paid</span>
                <span className="text-lg font-bold text-blue-600">SAR {stats.totalRentIn.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">This month: SAR {stats.totalRentIn.toLocaleString()}</span>
                <span className="text-red-600">Overdue: SAR {rentsWePay.filter(r => r.status === 'overdue').reduce((sum, r) => sum + r.amount, 0).toLocaleString()}</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Upcoming Payments</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {rentsWePay.map((rent) => {
                  const TypeIcon = getTypeIcon(rent.propertyType);
                  return (
                    <motion.div
                      key={rent.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      whileHover={{ scale: 1.01 }}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="p-2 rounded-lg bg-purple-100">
                            <TypeIcon className="w-4 h-4 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{rent.propertyName}</p>
                            <p className="text-xs text-gray-600">{rent.landlordName}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">{rent.city}</span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">Due: {rent.dueDate}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">SAR {rent.amount.toLocaleString()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(rent.status)}`}>
                            {rent.status === 'overdue' ? `${Math.abs(rent.daysUntilDue)} days overdue` :
                             rent.daysUntilDue === 0 ? 'Due today' :
                             `${rent.daysUntilDue} days`}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Expiring Contracts & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expiring Contracts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Expiring Contracts</h2>
            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
              {expiringContracts.filter(c => c.status === 'expiring-soon').length} Soon
            </span>
          </div>
          <div className="space-y-3">
            {expiringContracts.map((contract) => (
              <motion.div
                key={contract.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900">{contract.propertyName}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(contract.status)}`}>
                        {contract.type === 'renting-out' ? 'Renting Out' : 'Renting'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {contract.type === 'renting-out' ? contract.tenantName : (contract.landlordName || 'N/A')}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Ends: {contract.contractEnd}</span>
                      <span>•</span>
                      <span>Rent: SAR {contract.monthlyRent.toLocaleString()}/month</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {contract.status === 'expiring-soon' && (
                      <span className="text-sm font-semibold text-orange-600">
                        {contract.daysUntilExpiry} days
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6">Recent Activity</h2>
          <div className="space-y-4">
            {recentActivity.map((activity) => {
              const Icon = activity.icon;
              const colorClasses = {
                green: 'bg-green-100 text-green-600',
                orange: 'bg-orange-100 text-orange-600',
                blue: 'bg-blue-100 text-blue-600',
                purple: 'bg-purple-100 text-purple-600',
              };
              return (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  whileHover={{ scale: 1.01 }}
                  className="flex items-start gap-3"
                >
                  <div className={`p-2 rounded-lg ${colorClasses[activity.color]}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.message}</p>
                    <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Overview;
